import { Link } from 'react-router-dom'
import type { DashboardFeedItem } from '../../types'

// LiveFeed - reverse-chronological feed of mutations across the org. Backed by
// dbo.AuditLog once spec 08 ships; until then the API returns []. The empty
// state is the working v1 view (matches the prototype's "No activity yet"
// state in the empty-system bootstrap).

interface Props {
  feed: DashboardFeedItem[]
  loading: boolean
  showSeeAll: boolean    // gate the "see all" link by admin
}

export function LiveFeed({ feed, loading, showSeeAll }: Props) {
  return (
    <div className="bg-white border border-line rounded">
      <div className="px-5 py-3 border-b border-line flex items-center justify-between">
        <h2 className="font-display text-lg">Live activity</h2>
        {showSeeAll && (
          <Link to="/admin/audit" className="text-xs text-green font-semibold hover:underline">
            See all →
          </Link>
        )}
      </div>
      <div className="px-5 py-4">
        {loading && (
          <div className="text-sm text-ink-soft font-mono">Loading…</div>
        )}
        {!loading && feed.length === 0 && (
          <div className="text-sm text-ink-soft italic">
            No activity yet. Mutations across events appear here as they happen.
          </div>
        )}
        {!loading && feed.length > 0 && (
          <ul className="space-y-3">
            {feed.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <div className="w-7 h-7 rounded-full bg-green text-parchment text-xs font-semibold flex items-center justify-center flex-shrink-0">
                  {item.actor.avatarInitials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="leading-snug">
                    <span className="font-semibold">{item.actor.name}</span>{' '}
                    <span className="text-ink-soft">{item.summary}</span>
                  </div>
                  <div className="text-[11px] text-ink-soft font-mono mt-0.5">
                    {relativeTime(item.timestamp)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const diffSec = Math.round((Date.now() - then) / 1000)
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`
  if (diffSec < 86_400) return `${Math.round(diffSec / 3600)} hr ago`
  return `${Math.round(diffSec / 86_400)} days ago`
}
